import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'accesso',
  titolo: 'Entrare: invito, password e secondo fattore',
  gruppo: 'Per iniziare',
  permesso: null,
  aCheServe: 'Come si entra la prima volta con l’invito, come si entra i giorni dopo con il nome utente, come si '
    + 'attiva il secondo fattore, come si fa ricordare il proprio computer per entrare con Windows Hello o Touch ID, a '
    + 'che cosa serve l’email di recupero e che cosa fare se la password non funziona più o se hai perso il telefono.',
  parti: [
    {
      titolo: 'Il primo accesso con l’invito',
      testo: [
        'Ricevi due cose, e servono tutte e due: un **collegamento**, di solito per email (oggetto «Il tuo '
          + 'accesso all’ERP CIDIS»), e un **codice di sei cifre** che chi ti ha invitato ti detta a voce. Il '
          + 'codice non arriva mai per email.',
        'Se ti hanno creato la casella CIDIS, l’invito arriva alla tua **email personale**, quella che hai dato a chi '
          + 'ti ha aggiunto: la casella CIDIS la apri dopo, dal gestionale.',
      ],
      passi: [
        'Apri il collegamento per intero. La pagina ti saluta per nome e, se hai la casella CIDIS, ti dice il tuo '
          + '**nome utente** (per esempio «dramondetti»).',
        'Scrivi il codice in «Codice ricevuto a voce».',
        'Scegli la password in «Scegli una password» e riscrivila in «Scrivila di nuovo». Servono almeno 12 '
          + 'caratteri: una frase che ricordi va meglio di una parola complicata.',
        'Premi «Entra». Si apre il Cruscotto.',
      ],
      nota: 'L’invito vale 24 ore. Dopo cinque codici sbagliati si chiude. Se accetti l’invito arrivato alla tua email '
        + 'personale, quella diventa la tua email di recupero, già verificata.',
    },
    {
      titolo: 'Entrare i giorni dopo',
      passi: [
        'Apri il gestionale. Nella linguetta «Password» scrivi «Nome utente o email» e «Password». Con la casella '
          + 'CIDIS basta il nome utente, per esempio «dramondetti»: accanto c’è già «@cidisonlus.org». I '
          + 'collaboratori esterni scelgono nel menu accanto «@esterni.cidisonlus.org».',
        'Premi «Entra».',
        'Si apre la pagina «Secondo fattore»: scrivi il codice di sei cifre dell’app di autenticazione e premi '
          + '«Entra».',
      ],
      immagine: {
        file: 'accesso.webp', larghezza: 1200, altezza: 760,
        descrizione: 'La pagina di accesso con le linguette Password, Collegamento e Codice.',
      },
      nota: 'Chi entra con un’email non di CIDIS (per esempio un mediatore) la scrive per intero: appena scrivi la '
        + 'chiocciola, «@cidisonlus.org» sparisce. Le linguette «Collegamento» e «Codice» mandano per email un '
        + 'collegamento (vale 15 minuti) o un codice (vale 10 minuti), con lo stesso campo.',
    },
    {
      titolo: 'Ricordami su questo computer',
      testo: [
        'Sul **tuo** computer puoi entrare senza password né codice del secondo fattore: ti riconosce Windows Hello '
          + '(impronta, volto o PIN di Windows) o, sul Mac, Touch ID o la password del Mac. Vale 30 giorni dall’ultimo '
          + 'uso, e sul computer ricordato la sessione resta aperta 4 ore senza attività invece di una.',
        '**Solo sul tuo computer, non su quelli condivisi**: chi sa sbloccare quel computer entra come te.',
      ],
      passi: [
        'Entra come sempre, con la password e, se l’hai attivato, il secondo fattore.',
        'In basso compare «Ricordami su questo computer»: premi «Ricordami».',
        'Il computer chiede Windows Hello o Touch ID: conferma con l’impronta, il volto o il PIN.',
        'La volta dopo, nella pagina di accesso premi «Entra con Windows Hello» (sul Mac «Entra con Touch ID») e '
          + 'conferma. Sotto puoi sempre entrare con nome utente e password.',
      ],
      nota: 'Compare solo nel browser di un computer che ha Windows Hello o Touch ID. «Non ora» la nasconde per 30 '
        + 'giorni; puoi ricordare il computer quando vuoi da «Il mio profilo» → «Computer ricordati» → '
        + '«Ricorda questo computer». Da lì vedi i computer ricordati, con l’ultimo uso, e «Revoca» toglie quello '
        + 'che non usi più. Se cambi la password, si revocano tutti, tranne quello da cui la cambi.',
    },
    {
      titolo: 'Il secondo fattore: perché attivarlo',
      testo: [
        'Il secondo fattore è il codice di sei cifre che un’app di autenticazione sul telefono cambia ogni trenta '
          + 'secondi: chi scopre la tua password, senza il tuo telefono, non entra.',
        'Non è obbligatorio: si entra anche senza. Finché non l’hai attivato, a ogni accesso una riga in cima alla '
          + 'pagina dice «Proteggi il tuo accesso» e porta a «Secondo fattore»; la chiudi con la crocetta.',
        'Ti consigliamo di attivarlo, soprattutto se lavori con Direzione, amministrazione o privacy: con il tuo '
          + 'accesso si leggono i dati di molte persone.',
      ],
    },
    {
      titolo: 'Attivare il secondo fattore',
      passi: [
        'Installa sul telefono un’app di autenticazione, per esempio Google Authenticator, Microsoft Authenticator '
          + 'o FreeOTP.',
        'Nel menu, gruppo «Il tuo accesso», apri «Secondo fattore» (oppure segui l’avviso). Premi «Attiva il secondo '
          + 'fattore», poi «Comincia».',
        'Nell’app premi «Aggiungi» (o «+») e inquadra il codice QR. Se non riesci, o usi il gestionale dal telefono, '
          + 'scegli di inserire la chiave a mano e scrivi quella sotto il codice QR.',
        'Scrivi in «Codice dell’app» il codice che vedi adesso e premi «Conferma».',
        'Compaiono dieci **codici di recupero**: si vedono una volta sola. Premi «Stampa» o «Copia», tienili lontano '
          + 'dal telefono, metti la spunta e premi «Fatto».',
      ],
      immagine: {
        file: 'secondo-fattore.webp', larghezza: 1200, altezza: 800,
        descrizione: 'La pagina «Secondo fattore» con il codice QR, la chiave da scrivere a mano e il campo «Codice '
          + 'dell’app».',
      },
      nota: 'Se il codice viene rifiutato, controlla che l’ora del telefono sia giusta e scrivi quello che vedi adesso.',
    },
    {
      titolo: 'Senza telefono: i codici di recupero',
      passi: [
        'Dopo la password, nella pagina «Secondo fattore» premi «Usa un codice di recupero».',
        'Scrivi uno dei codici che hai conservato (per esempio «k7m2p-xq9ra»; maiuscole e trattino non contano) e '
          + 'premi «Entra».',
        'Il gestionale dice quanti codici ti restano. Ogni codice vale una volta sola.',
      ],
      nota: 'Quando ne restano pochi, in «Secondo fattore» premi «Genera nuovi codici di recupero»: quelli di prima '
        + 'smettono di valere.',
    },
    {
      titolo: 'Telefono nuovo o telefono perso',
      testo: [
        '**Telefono nuovo**: in «Secondo fattore» premi «Sostituisci l’app» e ripeti l’attivazione con il telefono '
          + 'nuovo. Fino alla conferma vale l’app di prima; i codici di recupero restano quelli.',
        '**Telefono perso**: entra con un codice di recupero e premi «Sostituisci l’app». Se non hai più codici, '
          + 'chiedi a chi gestisce gli utenti di azzerare il secondo fattore: al prossimo accesso ne attivi uno nuovo.',
      ],
    },
    {
      titolo: 'L’email di recupero',
      testo: [
        'È la tua email personale, non quella di CIDIS: se dimentichi la password, il collegamento per sceglierne una '
          + 'nuova arriva lì. La vede anche chi gestisce gli utenti, ma la cambi solo tu.',
        'Se lavori in CIDIS e non l’hai ancora, a ogni accesso una riga in cima alla pagina dice «Aggiungi la tua email '
          + 'personale di recupero»; la chiudi con la crocetta.',
      ],
      passi: [
        'Nel menu, gruppo «Il tuo accesso», apri «Il mio profilo» (oppure segui l’avviso).',
        'In «Email di recupero» scrivi la tua email personale e premi «Mandami il codice».',
        'Nell’email «Conferma l’email di recupero» trovi un codice di sei cifre: scrivilo in «Codice ricevuto» e premi '
          + '«Conferma». Vale mezz’ora.',
      ],
      nota: 'Per cambiarla premi «Cambia l’email di recupero» e ripeti: quella di prima vale finché non confermi la nuova.',
    },
    {
      titolo: 'Password dimenticata',
      testo: [
        'La password non si reimposta da soli: la richiesta la approva chi gestisce gli utenti.',
      ],
      passi: [
        'Nella linguetta «Password» premi «Ho dimenticato la password».',
        'Scrivi il tuo nome utente o la tua email e premi «Chiedi la reimpostazione». La pagina risponde sempre allo '
          + 'stesso modo; sulla casella CIDIS ti arriva un avviso della richiesta.',
        'Quando chi gestisce gli utenti la approva, alla tua email di recupero arriva «Reimpostazione della password '
          + 'approvata»: apri il collegamento entro un’ora.',
        'Scegli la password nuova, riscrivila e premi «Salva la password». Poi entra: se hai il secondo fattore, te lo '
          + 'chiede come sempre.',
      ],
      nota: 'Senza un’email di recupero verificata, chi gestisce gli utenti ti contatta e ti consegna un nuovo invito: '
        + 'collegamento e codice, come la prima volta.',
    },
    {
      titolo: 'Uscire',
      testo: [
        'Premi «Esci», in alto a destra, soprattutto da un computer che usano anche altri. Dopo un’ora senza '
          + 'attività la sessione si chiude da sola; sul computer ricordato dopo 4 ore.',
        'Sul computer ricordato «Esci» chiude la sessione ma non dimentica il computer: la volta dopo entri con '
          + 'Windows Hello o Touch ID. Per dimenticarlo usa «Revoca» in «Il mio profilo».',
        '«Esci» non chiede conferma: se stai registrando un intervento, salva prima.',
      ],
    },
  ],
  avvertenze: [
    {
      quando: '«Il collegamento dell’invito è incompleto: ne manca una parte.»',
      cosaFare: 'Il collegamento è stato copiato a metà. Riaprilo dall’email, oppure copialo fino all’ultimo carattere.',
    },
    {
      quando: 'L’invito è scaduto, oppure hai sbagliato il codice cinque volte.',
      cosaFare: 'Chiedi a chi ti ha invitato un «Nuovo invito». Il collegamento vecchio smette di valere.',
    },
    {
      quando: '«Questo invito è già stato usato: entra con la tua password.»',
      cosaFare: 'Hai già scelto la password: entra dalla pagina di accesso. Se non la ricordi, «Ho dimenticato la password».',
    },
    {
      quando: 'Hai chiesto la reimpostazione della password e non arriva niente.',
      cosaFare: 'La richiesta aspetta l’approvazione di chi gestisce gli utenti: chiedi a loro. Il collegamento arriva '
        + 'solo all’email di recupero verificata; senza, ti consegnano un nuovo invito.',
    },
    {
      quando: 'Ti è arrivato l’avviso «Richiesta di reimpostazione della password» ma non l’hai chiesta tu.',
      cosaFare: 'Avvisa chi gestisce gli utenti: la respinge. La tua password resta valida.',
    },
    {
      quando: '«L’email personale non può essere un indirizzo …@cidisonlus.org»',
      cosaFare: 'L’email di recupero serve quando la casella CIDIS non si apre: scrivi un indirizzo personale.',
    },
    {
      quando: '«Troppi tentativi: riprova fra … secondi.»',
      cosaFare: 'Dopo alcuni errori l’accesso si blocca per poco tempo. Aspetta e riprova.',
    },
    {
      quando: 'Il codice del secondo fattore viene rifiutato come già usato.',
      cosaFare: 'Lo stesso codice non vale due volte: aspetta quello successivo.',
    },
    {
      quando: '«Codice di recupero non valido, oppure già usato.»',
      cosaFare: 'Ogni codice vale una volta sola, e dopo «Genera nuovi codici di recupero» valgono solo quelli nuovi. '
        + 'Prova un altro codice; se non ne hai più, chiedi a chi gestisce gli utenti di azzerare il secondo fattore.',
    },
    {
      quando: 'Ti ritrovi nella pagina di accesso mentre lavoravi.',
      cosaFare: 'La sessione si è chiusa dopo un’ora senza attività (4 ore sul computer ricordato). Entra di nuovo.',
    },
    {
      quando: '«Entra con Windows Hello» (o «con Touch ID») non compare più.',
      cosaFare: 'Il computer non è più ricordato: sono passati 30 giorni dall’ultimo uso, hai cambiato la password, '
        + 'l’hai revocato o hai cancellato i dati del browser. Entra con la password e premi di nuovo «Ricordami».',
    },
    {
      quando: '«Questo computer è ricordato per …» con il nome di un’altra persona.',
      cosaFare: 'Quel computer l’ha ricordato un collega. Entra con nome utente e password; se il computer è condiviso, '
        + 'diglielo, perché lo revochi da «Il mio profilo».',
    },
    {
      quando: 'Windows Hello o Touch ID non conferma, oppure non compare.',
      cosaFare: 'Premi di nuovo il pulsante. Se il computer non ha più l’impronta o il PIN configurati, entra con nome '
        + 'utente e password.',
    },
  ],
  vediAnche: ['cruscotto', 'telefono'],
  parole: ['login', 'credenziali', 'codice', 'mfa', 'autenticazione', 'invito', 'sessione', 'esci',
    'secondo fattore', '2fa', 'totp', 'codice qr', 'codici di recupero', 'telefono perso', 'authenticator',
    'nome utente', 'dominio', 'email di recupero', 'email personale', 'password dimenticata', 'il mio profilo',
    'ricordami', 'ricorda questo computer', 'computer ricordati', 'windows hello', 'touch id', 'passkey', 'revoca'],
}

export default capitolo
